import { useCallback, useMemo, useState } from 'react';
import { Campaign, CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { useDeleteCampaign } from '@frontend/api-bulk-messaging';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { BulkMessagingPrefixes } from '@frontend/tracking-prefixes';
import { Table, TableColumnConfig, TableRowActions, Text, useModalControl } from '@frontend/design-system';
import { useBulkNavigator } from '../../hooks/use-bulk-navigator';
import { DeleteCampaignModal } from '../modals/delete-campaign-modal';

type Props = {
  drafts: Campaign[];
  hasAllotment: boolean;
};

// TODO: Implement pagination and backend sorting. Remove client-side sorting.
export const DraftsTable = ({ drafts, hasAllotment }: Props) => {
  const { t } = useTranslation('bulk-messaging');
  const { selectedOrgId: orgId } = useAppScopeStore();

  const navigateToEmail = useBulkNavigator(CampaignType.EMAIL);
  const navigateToText = useBulkNavigator(CampaignType.SMS);
  const navigateTo = useMemo(() => {
    return {
      editor: (draft: Campaign) => {
        if (draft.type === CampaignType.EMAIL) {
          navigateToEmail.editor({ campaignId: draft.campaignId });
        } else {
          navigateToText.editor({ campaignId: draft.campaignId });
        }
      },

      duplicate: (draft: Campaign, copyAudience?: boolean) => {
        if (draft.type === CampaignType.EMAIL) {
          navigateToEmail.editor({ seedCampaignId: draft.campaignId, isCopyingAudience: copyAudience });
        } else {
          navigateToText.editor({ seedCampaignId: draft.campaignId, isCopyingAudience: copyAudience });
        }
      },
    };
  }, [navigateToEmail, navigateToText]);

  const [draftToDelete, setDraftToDelete] = useState<Campaign | undefined>(undefined);
  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();
  const { mutate: deleteCampaign } = useDeleteCampaign(true);

  const showDeleteDialog = useCallback(
    (draft: Campaign) => {
      setDraftToDelete(draft);
      deleteTriggerProps.onClick();
    },
    [deleteTriggerProps]
  );

  const onDelete = useCallback(() => {
    if (!draftToDelete || !draftToDelete.campaignId) return;
    deleteCampaign({ orgId, campaignId: draftToDelete.campaignId });
    setDraftToDelete(undefined);
    deleteModalProps.onClose();
  }, [draftToDelete, deleteCampaign, orgId, deleteModalProps]);

  const onCancel = useCallback(() => {
    setDraftToDelete(undefined);
    deleteModalProps.onClose();
  }, [deleteModalProps]);

  const colConfig: TableColumnConfig<Campaign>[] = useMemo(
    () => [
      {
        Header: t('Date Created'),
        accessor: ({ createdAt }) => formatDate(createdAt ?? '', 'MMMM Do, YYYY'),
        disableSortBy: true,
        id: 'dateCreated',
        width: 75,
      },
      {
        Header: t('Campaign Title'),
        accessor: ({ title }) => title,
        cellConfig: {
          element: 'TextLink',
          onClick: (_, draft) => navigateTo.editor(draft),
          trackingId: `${BulkMessagingPrefixes.Drafts}-table-name-link`,
        },
        disableSortBy: true,
        id: 'campaignTitle',
        width: 100,
      },
      {
        Header: t('Message Type'),
        accessor: ({ type }) => (type === CampaignType.EMAIL ? t('Email') : t('Text')),
        disableSortBy: true,
        id: 'messageType',
        width: 100,
      },
    ],
    [t, navigateTo]
  );

  const rowActions: TableRowActions<Campaign> = useMemo(
    () => ({
      rowActionMenuLabel: '',
      actions: [
        {
          label: t('Edit Draft'),
          Icon: () => <Icon name='edit' />,
          onClick: (draft) => navigateTo.editor(draft),
          trackingId: `${BulkMessagingPrefixes.Drafts}-table-edit-action`,
        },
        {
          label: t('Duplicate Draft'),
          hide: !hasAllotment,
          Icon: () => <Icon name='copy' />,
          onClick: (draft) => navigateTo.duplicate(draft),
          trackingId: `${BulkMessagingPrefixes.Drafts}-table-duplicate-action`,
        },
        // { // TODO: Enable this after implementing duplicate with recipients in the duplicate view
        //   label: t('Duplicate with Recipients'),
        //   hide: !hasAllotment,
        //   Icon: () => <Icon name='family' />,
        //   onClick: (draft) => navigateTo.duplicate(draft, true),
        //   trackingId: `${BulkMessagingPrefixes.Drafts}-table-duplicate-with-recipients-action`,
        // },
        {
          label: <Text color='error'>{t('Delete Draft')}</Text>,
          Icon: () => <Icon name='trash' color='error' />,
          onClick: (draft) => showDeleteDialog(draft),
          trackingId: `${BulkMessagingPrefixes.Drafts}-table-delete-action`,
        },
      ],
    }),
    [t, hasAllotment, navigateTo, showDeleteDialog]
  );

  return (
    <>
      <Table<Campaign> colConfig={colConfig} data={drafts} rowActions={rowActions} />
      <DeleteCampaignModal
        {...deleteModalProps}
        campaign={draftToDelete}
        onCancel={onCancel}
        onClose={onCancel}
        onDelete={onDelete}
        trackingIdPrefix={BulkMessagingPrefixes.Drafts}
      />
    </>
  );
};
